const initOrderSummaryComponent = () => {
    if (typeof window.antd === 'undefined') {
        setTimeout(initOrderSummaryComponent, 100);
        return;
    }

    const { 
        ConfigProvider,
        theme,
        Radio,
        Select,
        Upload,
        Button,
        Spin,
        message,
        Tooltip
    } = window.antd;
    
    const { LoadingOutlined, DeleteOutlined, UploadOutlined } = window.icons;

    // Cloudinary configuration
    const CLOUDINARY_CLOUD_NAME = 'dhzzomdy0';
    const CLOUDINARY_UPLOAD_PRESET = 'musashi-engravings';

    const SHAPE_NAMES = {
        'santoku': 'Santoku',
        'nakiri': 'Nakiri',
        'gyuto': 'Gyuto',
        'bunka': 'Bunka',
        'kobunka': 'Kobunka',
        'petty': 'Petty',
        'kiritsuke-petty': 'Kiritsuke Petty',
        'kiritsuke': 'Kiritsuke'
    };

    function OrderSummaryComplete() {
        const [selectedShape, setSelectedShape] = React.useState(null);
        const [selectedBlade, setSelectedBlade] = React.useState(null);
        const [selectedHandle, setSelectedHandle] = React.useState(null);
        const [bladeData, setBladeData] = React.useState(null);
        const [handleData, setHandleData] = React.useState(null);
        const [engravingRequired, setEngravingRequired] = React.useState('no');
        const [engravingLocation, setEngravingLocation] = React.useState('');
        const [uploadedFile, setUploadedFile] = React.useState(null);
        const [uploading, setUploading] = React.useState(false);
        const [handleFittingRequired, setHandleFittingRequired] = React.useState('yes');
        
        // Custom Knife Box state
        const [customBoxRequired, setCustomBoxRequired] = React.useState('no');
        const [customBoxColor, setCustomBoxColor] = React.useState('');
        const [customBoxFile, setCustomBoxFile] = React.useState(null);
        const [customBoxUploading, setCustomBoxUploading] = React.useState(false);
        
        const handleFittingPrice = 1000;
        const engravingPrice = 500;
        const customBoxPrice = 30000;
        
        const engravingLocations = [
            { value: 'blade-center', label: 'Center of Blade' },
            { value: 'blade-base', label: 'Base of Blade' }
        ];
        
        const customBoxColors = [
            { value: 'red', label: 'Red' },
            { value: 'white', label: 'White' }
        ];

        const hasAllKnifeComponents = selectedShape && selectedBlade && selectedHandle;
        const hasBlade = selectedBlade && bladeData;
        const hasHandle = selectedHandle && handleData;
        const hasBladeAndHandle = hasBlade && hasHandle;
        const hasAnySelection = hasBlade || hasHandle;

        const formatPrice = (price) => {
            if (window.CurrencyFormat && window.CurrencyFormat.formatMoney) {
                return window.CurrencyFormat.formatMoney(price || 0);
            }
            return '¥' + (price || 0).toLocaleString() + ' JPY';
        };

        const formatPricePerKnife = (price) => {
            return formatPrice(price) + '/knife';
        };
        
        React.useEffect(() => {
            const checkState = setInterval(() => {
                if (!window.KnifeCustomizationState) return;
                
                if (window.KnifeCustomizationState.selectedShape !== selectedShape) {
                    setSelectedShape(window.KnifeCustomizationState.selectedShape);
                }
                
                if (window.KnifeCustomizationState.selectedBlade !== selectedBlade) {
                    setSelectedBlade(window.KnifeCustomizationState.selectedBlade);
                }
                if (window.KnifeCustomizationState.selectedBladeData !== bladeData) {
                    setBladeData(window.KnifeCustomizationState.selectedBladeData);
                }
                
                if (window.KnifeCustomizationState.selectedHandle !== selectedHandle) {
                    setSelectedHandle(window.KnifeCustomizationState.selectedHandle);
                }
                if (window.KnifeCustomizationState.selectedHandleData !== handleData) {
                    setHandleData(window.KnifeCustomizationState.selectedHandleData);
                }
                
                window.KnifeCustomizationState.engravingRequired = engravingRequired;
                window.KnifeCustomizationState.engravingLocation = engravingLocation;
                window.KnifeCustomizationState.engravingImage = uploadedFile;
                window.KnifeCustomizationState.handleFittingRequired = handleFittingRequired;
                
                // Custom Box state sync
                window.KnifeCustomizationState.customBoxRequired = customBoxRequired;
                window.KnifeCustomizationState.customBoxColor = customBoxColor;
                window.KnifeCustomizationState.customBoxImage = customBoxFile;

                if (hasBladeAndHandle) {
                    window.KnifeCustomizationState.handleFittingPrice = handleFittingRequired === 'yes' ? handleFittingPrice : 0;
                } else {
                    window.KnifeCustomizationState.handleFittingPrice = 0;
                }

                if (hasBlade) {
                    window.KnifeCustomizationState.engravingPrice = engravingRequired === 'yes' ? engravingPrice : 0;
                } else {
                    window.KnifeCustomizationState.engravingPrice = 0;
                }
                
                if (hasAnySelection) {
                    window.KnifeCustomizationState.customBoxPrice = customBoxRequired === 'yes' ? customBoxPrice : 0;
                } else {
                    window.KnifeCustomizationState.customBoxPrice = 0;
                }
            }, 100);
            
            return () => clearInterval(checkState);
        }, [selectedShape, selectedBlade, selectedHandle, bladeData, handleData, engravingRequired, engravingLocation, uploadedFile, handleFittingRequired, hasBlade, hasBladeAndHandle, hasAnySelection, customBoxRequired, customBoxColor, customBoxFile]);
        
        const uploadToCloudinary = async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
            formData.append('folder', 'engravings');

            try {
                const response = await fetch(
                    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
                    {
                        method: 'POST',
                        body: formData
                    }
                );

                if (!response.ok) {
                    throw new Error('Upload failed');
                }

                const data = await response.json();
                return data.secure_url;
            } catch (error) {
                console.error('Cloudinary upload error:', error);
                throw error;
            }
        };

        const uploadProps = {
            name: 'file',
            multiple: false,
            accept: 'image/svg+xml',
            showUploadList: false,
            beforeUpload: async (file) => {
                const isSVG = file.type === 'image/svg+xml';
                if (!isSVG) {
                    message.error('You can only upload SVG files!');
                    return false;
                }
                const isLt1M = file.size / 1024 / 1024 < 1;
                if (!isLt1M) {
                    message.error('Image must be smaller than 1MB!');
                    return false;
                }
                
                setUploading(true);
                
                try {
                    const cloudinaryUrl = await uploadToCloudinary(file);
                    setUploadedFile({
                        name: file.name,
                        size: file.size,
                        cloudinaryUrl: cloudinaryUrl
                    });
                    message.success('Logo uploaded successfully!');
                } catch (error) {
                    message.error('Failed to upload logo. Please try again.');
                } finally {
                    setUploading(false);
                }
                
                return false;
            },
        };
        
        // Custom Box upload props
        const customBoxUploadProps = {
            name: 'file',
            multiple: false,
            accept: 'image/svg+xml',
            showUploadList: false,
            beforeUpload: async (file) => {
                const isSVG = file.type === 'image/svg+xml';
                if (!isSVG) {
                    message.error('You can only upload SVG files!');
                    return false;
                }
                const isLt1M = file.size / 1024 / 1024 < 1;
                if (!isLt1M) {
                    message.error('Image must be smaller than 1MB!');
                    return false;
                }
                
                setCustomBoxUploading(true);
                
                try {
                    const cloudinaryUrl = await uploadToCloudinary(file);
                    setCustomBoxFile({
                        name: file.name,
                        size: file.size,
                        cloudinaryUrl: cloudinaryUrl
                    });
                    message.success('Logo uploaded successfully!');
                } catch (error) {
                    message.error('Failed to upload logo. Please try again.');
                } finally {
                    setCustomBoxUploading(false);
                }
                
                return false;
            },
        };
        
        const loadingIcon = React.createElement(LoadingOutlined, {
            style: { fontSize: 24, color: 'rgba(255, 255, 255, 0.6)' },
            spin: true,
        });

        const disabledStyle = {
            opacity: 0.4,
            pointerEvents: 'none'
        };

        const shapeName = selectedShape ? SHAPE_NAMES[selectedShape] || selectedShape : null;
        const bladeName = bladeData ? bladeData.name : null;
        const bladePrice = bladeData ? bladeData.price : 0;
        const handleName = handleData ? handleData.name : null;
        const handlePrice = handleData ? handleData.price : 0;
        
        return React.createElement(ConfigProvider, {
            theme: {
                algorithm: theme.darkAlgorithm,
                token: {
                    colorPrimary: 'rgba(255, 255, 255, 0.6)',
                    colorBgContainer: 'transparent',
                    colorBorder: 'rgba(255, 255, 255, 0.2)',
                    colorText: 'rgba(255, 255, 255, 0.6)',
                    colorTextSecondary: 'rgba(255, 255, 255, 0.45)',
                    colorPrimaryHover: 'rgba(255, 255, 255, 0.8)',
                    colorPrimaryActive: 'rgba(255, 255, 255, 0.4)',
                    fontSize: 12,
                    borderRadius: 2,
                    controlHeight: 40,
                },
            }
        },
            React.createElement('div', { className: 'summary--lines-wrapper' },
                React.createElement('div', { className: 'summary-line' },
                    React.createElement('div', { className: 'summary--line-top' },
                        React.createElement('div', { className: 'summary--small-text' }, 'KNIFE SHAPE'),
                        React.createElement('div', { className: 'summary--small-text' }, '')
                    ),
                    React.createElement('div', { className: 'summary--small-text _60' },
                        shapeName || 'Not selected'
                    )
                ),
                React.createElement('div', { className: 'summary-separator' }, '\u00A0'),
                
                React.createElement('div', { className: 'summary-line' },
                    React.createElement('div', { className: 'summary--line-top' },
                        React.createElement('div', { className: 'summary--small-text' }, 'BLADE TYPE'),
                        React.createElement('div', { className: 'summary--small-text' },
                            formatPrice(bladePrice)
                        )
                    ),
                    React.createElement('div', { className: 'summary--small-text _60' },
                        bladeName || 'Not selected'
                    )
                ),
                React.createElement('div', { className: 'summary-separator' }, '\u00A0'),
                
                React.createElement('div', { className: 'summary-line' },
                    React.createElement('div', { className: 'summary--line-top' },
                        React.createElement('div', { className: 'summary--small-text' }, 'HANDLE TYPE'),
                        React.createElement('div', { className: 'summary--small-text' },
                            formatPrice(handlePrice)
                        )
                    ),
                    React.createElement('div', { className: 'summary--small-text _60' },
                        handleName || 'Not selected'
                    )
                ),
                React.createElement('div', { className: 'summary-separator' }, '\u00A0'),
                
                React.createElement('div', { 
                    className: 'summary-line',
                    style: hasBladeAndHandle ? {} : disabledStyle
                },
                    React.createElement('div', { className: 'summary--line-top' },
                        React.createElement('div', { 
                            className: 'summary--small-text',
                            style: { display: 'flex', alignItems: 'center', gap: '8px' }
                        },
                            'HANDLE FITTING SERVICE',
                            React.createElement(Tooltip, {
                                title: React.createElement('span', { className: 'summary--small-text _60' },
                                    hasBladeAndHandle 
                                        ? 'Professional assembly of blade and handle components. Without this service, parts are shipped separately for self-assembly.'
                                        : 'Please select blade type and handle type first.'
                                ),
                                placement: 'top',
                                overlayStyle: { maxWidth: '250px' },
                                overlayInnerStyle: { 
                                    backgroundColor: '#191919',
                                    padding: '8px 12px',
                                    borderRadius: '2px'
                                },
                                color: '#191919'
                            },
                                React.createElement('img', {
                                    src: 'https://cdn.prod.website-files.com/68d5e9e73859fd3ba297c9c2/68db4ef3733a819133e47c95_card--tool-tip.svg',
                                    alt: 'tooltip',
                                    className: 'knife-tooltip',
                                    style: { 
                                        width: '16px', 
                                        height: '16px', 
                                        cursor: 'help',
                                        opacity: 0.8,
                                        top: '0px',
                                        left: '0px',
                                        position: 'relative',
                                        display: 'inline-block',
                                        verticalAlign: 'middle'
                                    }
                                })
                            )
                        ),
                        React.createElement('div', { 
                            className: 'summary--small-text',
                            style: hasBladeAndHandle && handleFittingRequired === 'no' ? { opacity: 0.4 } : {}
                        },
                            formatPricePerKnife(handleFittingPrice)
                        )
                    ),
                    React.createElement('div', { style: { marginTop: '12px' } },
                        React.createElement(Radio.Group, {
                            onChange: (e) => setHandleFittingRequired(e.target.value),
                            value: handleFittingRequired,
                            disabled: !hasBladeAndHandle
                        },
                            React.createElement(Radio, { value: 'yes' },
                                React.createElement('span', { className: 'summary--small-text _60' },
                                    'Required'
                                )
                            ),
                            React.createElement(Radio, { value: 'no' },
                                React.createElement('span', { className: 'summary--small-text _60' },
                                    'Not Required'
                                )
                            )
                        )
                    ),
                    !hasBladeAndHandle && React.createElement('div', { 
                        className: 'summary--small-text _60',
                        style: { marginTop: '8px', fontStyle: 'italic' }
                    }, 'Select blade and handle to enable')
                ),
                React.createElement('div', { className: 'summary-separator' }, '\u00A0'),
                
                React.createElement('div', { 
                    className: 'summary-line engraving-section',
                    style: hasBlade ? {} : disabledStyle
                },
                    React.createElement('div', { className: 'summary--line-top' },
                        React.createElement('div', { className: 'summary--small-text' }, 'CUSTOM ENGRAVING'),
                        React.createElement('div', { 
                            className: 'summary--small-text',
                            style: hasBlade && engravingRequired === 'no' ? { opacity: 0.4 } : {}
                        },
                            formatPricePerKnife(engravingPrice)
                        )
                    ),
                    React.createElement('div', { style: { marginTop: '12px' } },
                        React.createElement(Radio.Group, {
                            onChange: (e) => setEngravingRequired(e.target.value),
                            value: engravingRequired,
                            disabled: !hasBlade
                        },
                            React.createElement(Radio, { value: 'yes' },
                                React.createElement('span', { className: 'summary--small-text _60' },
                                    'Required'
                                )
                            ),
                            React.createElement(Radio, { value: 'no' },
                                React.createElement('span', { className: 'summary--small-text _60' },
                                    'Not Required'
                                )
                            )
                        )
                    ),
                    !hasBlade && React.createElement('div', { 
                        className: 'summary--small-text _60',
                        style: { marginTop: '8px', fontStyle: 'italic' }
                    }, 'Select blade to enable'),
                    hasBlade && engravingRequired === 'yes' && React.createElement('div', { style: { marginTop: '1em' } },
                        React.createElement('div', null,
                            React.createElement('div', { 
                                className: 'summary--small-text',
                                style: { marginBottom: '0.5em' }
                            }, 'ENGRAVING LOCATION'),
                            React.createElement(Select, {
                                style: { width: '100%' },
                                placeholder: 'Select engraving location',
                                value: engravingLocation || undefined,
                                onChange: (value) => setEngravingLocation(value),
                                options: engravingLocations,
                                size: 'middle'
                            })
                        ),
                        React.createElement('div', { style: { marginTop: '1em' } },
                            React.createElement('div', { 
                                className: 'summary--small-text',
                                style: { marginBottom: '0.5em' }
                            }, 'CUSTOM LOGO ENGRAVING'),
                            React.createElement('div', { 
                                className: 'summary--small-text _60',
                                style: { marginBottom: '16px' }
                            }, 'Upload your logo design in SVG format for precise engraving. Vector files ensure the highest quality reproduction on your custom knife. Max file size: 1MB.'),
                            uploading ? React.createElement('div', {
                                style: { textAlign: 'center', padding: '40px' }
                            },
                                React.createElement(Spin, { indicator: loadingIcon }),
                                React.createElement('div', {
                                    style: { marginTop: '12px', color: 'rgba(255, 255, 255, 0.45)', fontSize: '12px' }
                                }, 'Uploading...')
                            ) : uploadedFile ? React.createElement('div', {
                                style: { 
                                    padding: '12px',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    borderRadius: '2px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)'
                                }
                            },
                                React.createElement('div', {
                                    style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
                                },
                                    React.createElement('div', null,
                                        React.createElement('div', {
                                            style: { fontSize: '13px', color: 'rgba(255, 255, 255, 0.8)' }
                                        }, uploadedFile.name),
                                        React.createElement('div', {
                                            style: { fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }
                                        }, (uploadedFile.size / 1024).toFixed(2) + ' KB')
                                    ),
                                    React.createElement(Button, {
                                        size: 'small',
                                        icon: React.createElement(DeleteOutlined),
                                        onClick: () => setUploadedFile(null)
                                    }, 'Remove')
                                )
                            ) : React.createElement(Upload, uploadProps,
                                React.createElement(Button, {
                                    icon: React.createElement(UploadOutlined),
                                    style: {
                                        width: '100%',
                                        borderStyle: 'dashed',
                                        height: '40px'
                                    }
                                }, 'Click to Upload SVG')
                            )
                        )
                    )
                ),
                React.createElement('div', { className: 'summary-separator' }, '\u00A0'),
                
                // Custom Knife Box Section
                React.createElement('div', { 
                    className: 'summary-line custom-box-section',
                    style: hasAnySelection ? {} : disabledStyle
                },
                    React.createElement('div', { className: 'summary--line-top' },
                        React.createElement('div', { 
                            className: 'summary--small-text',
                            style: { display: 'flex', alignItems: 'center', gap: '8px' }
                        },
                            'CUSTOM KNIFE BOX',
                            React.createElement(Tooltip, {
                                title: React.createElement('span', { className: 'summary--small-text _60' },
                                    hasAnySelection 
                                        ? 'A one-time plate setup fee is required to create the custom embossing and foil stamp for your logo on the product box.'
                                        : 'Please select at least a blade or handle first.'
                                ),
                                placement: 'top',
                                overlayStyle: { maxWidth: '250px' },
                                overlayInnerStyle: { 
                                    backgroundColor: '#191919',
                                    padding: '8px 12px',
                                    borderRadius: '2px'
                                },
                                color: '#191919'
                            },
                                React.createElement('img', {
                                    src: 'https://cdn.prod.website-files.com/68d5e9e73859fd3ba297c9c2/68db4ef3733a819133e47c95_card--tool-tip.svg',
                                    alt: 'tooltip',
                                    className: 'knife-tooltip',
                                    style: { 
                                        width: '16px', 
                                        height: '16px', 
                                        cursor: 'help',
                                        opacity: 0.8,
                                        top: '0px',
                                        left: '0px',
                                        position: 'relative',
                                        display: 'inline-block',
                                        verticalAlign: 'middle'
                                    }
                                })
                            )
                        ),
                        React.createElement('div', { 
                            className: 'summary--small-text',
                            style: hasAnySelection && customBoxRequired === 'no' ? { opacity: 0.4 } : {}
                        },
                            formatPrice(customBoxPrice)
                        )
                    ),
                    React.createElement('div', { style: { marginTop: '12px' } },
                        React.createElement(Radio.Group, {
                            onChange: (e) => setCustomBoxRequired(e.target.value),
                            value: customBoxRequired,
                            disabled: !hasAnySelection
                        },
                            React.createElement(Radio, { value: 'yes' },
                                React.createElement('span', { className: 'summary--small-text _60' },
                                    'Required'
                                )
                            ),
                            React.createElement(Radio, { value: 'no' },
                                React.createElement('span', { className: 'summary--small-text _60' },
                                    'Not Required'
                                )
                            )
                        )
                    ),
                    !hasAnySelection && React.createElement('div', { 
                        className: 'summary--small-text _60',
                        style: { marginTop: '8px', fontStyle: 'italic' }
                    }, 'Select blade or handle to enable'),
                    hasAnySelection && customBoxRequired === 'yes' && React.createElement('div', { style: { marginTop: '1em' } },
                        React.createElement('div', null,
                            React.createElement('div', { 
                                className: 'summary--small-text',
                                style: { marginBottom: '0.5em' }
                            }, 'BOX COLOR'),
                            React.createElement(Select, {
                                style: { width: '100%' },
                                placeholder: 'Select box color',
                                value: customBoxColor || undefined,
                                onChange: (value) => setCustomBoxColor(value),
                                options: customBoxColors,
                                size: 'middle'
                            })
                        ),
                        React.createElement('div', { style: { marginTop: '1em' } },
                            React.createElement('div', { 
                                className: 'summary--small-text',
                                style: { marginBottom: '0.5em' }
                            }, 'CUSTOM LOGO FOR BOX'),
                            React.createElement('div', { 
                                className: 'summary--small-text _60',
                                style: { marginBottom: '16px' }
                            }, 'Upload your logo design in SVG format for printing on the custom knife box. Vector files ensure the highest quality reproduction. Max file size: 1MB.'),
                            customBoxUploading ? React.createElement('div', {
                                style: { textAlign: 'center', padding: '40px' }
                            },
                                React.createElement(Spin, { indicator: loadingIcon }),
                                React.createElement('div', {
                                    style: { marginTop: '12px', color: 'rgba(255, 255, 255, 0.45)', fontSize: '12px' }
                                }, 'Uploading...')
                            ) : customBoxFile ? React.createElement('div', {
                                style: { 
                                    padding: '12px',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    borderRadius: '2px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)'
                                }
                            },
                                React.createElement('div', {
                                    style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
                                },
                                    React.createElement('div', null,
                                        React.createElement('div', {
                                            style: { fontSize: '13px', color: 'rgba(255, 255, 255, 0.8)' }
                                        }, customBoxFile.name),
                                        React.createElement('div', {
                                            style: { fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }
                                        }, (customBoxFile.size / 1024).toFixed(2) + ' KB')
                                    ),
                                    React.createElement(Button, {
                                        size: 'small',
                                        icon: React.createElement(DeleteOutlined),
                                        onClick: () => setCustomBoxFile(null)
                                    }, 'Remove')
                                )
                            ) : React.createElement(Upload, customBoxUploadProps,
                                React.createElement(Button, {
                                    icon: React.createElement(UploadOutlined),
                                    style: {
                                        width: '100%',
                                        borderStyle: 'dashed',
                                        height: '40px'
                                    }
                                }, 'Click to Upload SVG')
                            )
                        )
                    )
                ),
                React.createElement('div', { className: 'summary-separator' }, '\u00A0')
            )
        );
    }
    
    window.OrderSummaryComplete = OrderSummaryComplete;
};

initOrderSummaryComponent();